import { SxProps, Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import createTicketTokens from "./CreateTicket.tokens";

const t = createTicketTokens;

/*
 * The dialog shell, header and close affordance are no longer defined here: the
 * form renders inside `AppModal`, which owns the paper, scrim, header and the
 * footer bar. Only the ticket form's own chrome remains below.
 */

/* ── Form layout ─────────────────────────────────────────────────────────── */

export const formSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.layout.fieldGap,
};

/** Category and Priority share one row and stack on a narrow viewport. */
export const fieldRowSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  gap: { xs: t.layout.fieldGap, sm: t.layout.columnGap },
};

export const fieldColumnSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const labelSx: SxProps<Theme> = {
  display: "block",
  mb: t.layout.labelMarginBottom,
  color: t.color.ink,
  fontSize: t.font.label,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
  // `InputLabel` renders the required star through this class.
  "& .MuiFormLabel-asterisk": { color: t.color.accent, ml: "2px" },
};

export const helperTextSx: SxProps<Theme> = {
  m: 0,
  mt: t.layout.helperMarginTop,
  color: t.color.error,
  fontSize: t.font.helper,
  fontWeight: t.weight.regular,
  lineHeight: "normal",
};

/* ── Fields ──────────────────────────────────────────────────────────────── */

/**
 * Shared chrome for the two selects and the summary input: a soft filled well
 * with a hairline outline that picks up the accent on focus.
 *
 * MUI paints the outline through a fieldset sibling, so the border states are
 * addressed on `.MuiOutlinedInput-notchedOutline` rather than on `border`.
 */
export const fieldSx = (hasError: boolean): SystemStyleObject<Theme> => ({
  width: "100%",
  height: t.field.height,
  bgcolor: t.color.fieldBg,
  borderRadius: t.radius.control,
  color: t.color.ink,
  fontSize: t.font.field,
  fontWeight: t.weight.medium,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: hasError ? t.color.error : t.color.fieldBorder,
    borderRadius: t.radius.control,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: hasError ? t.color.error : t.color.ink,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: hasError ? t.color.error : t.color.accent,
  },
  "& .MuiOutlinedInput-input, & .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "100%",
    boxSizing: "border-box",
    px: t.field.paddingX,
    py: 0,
    fontSize: t.font.field,
    fontWeight: t.weight.medium,
    lineHeight: "normal",
    color: t.color.ink,
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: t.color.placeholder,
    opacity: 1,
    fontWeight: t.weight.regular,
  },
  "&.Mui-error.Mui-focused": {
    outline: "2px solid",
    outlineColor: "formField.focusBorder",
    outlineOffset: "2px",
  },
});

/** Empty selects read as placeholders rather than as a chosen value. */
export const selectPlaceholderSx: SxProps<Theme> = {
  color: t.color.placeholder,
  fontWeight: t.weight.regular,
};

/**
 * MUI centres the select's chevron with `top: calc(50% - .5em)`, so the glyph
 * has to be sized through `fontSize` — setting width/height alone would leave
 * the arrow riding high.
 */
export const selectIconSx: SystemStyleObject<Theme> = {
  right: "12px",
  fontSize: "16px",
  color: t.color.subtle,
  pointerEvents: "none",
};

/** Keeps the chosen value clear of the chevron. */
export const selectValueSx: SystemStyleObject<Theme> = {
  pr: "36px",
};

export const selectMenuSx: SxProps<Theme> = {
  mt: "6px",
  maxHeight: t.field.menuMaxHeight,
  border: `1px solid ${t.color.fieldBorder}`,
  borderRadius: t.radius.control,
  boxShadow: "0 12px 28px rgba(33, 21, 71, 0.12)",
  "& .MuiMenuItem-root": {
    px: t.field.paddingX,
    py: "9px",
    fontSize: t.font.field,
    fontWeight: t.weight.medium,
    color: t.color.body,
    "&:hover": { bgcolor: t.color.closeHoverBg },
    "&.Mui-selected": {
      bgcolor: t.color.closeHoverBg,
      color: t.color.ink,
      "&:hover": { bgcolor: t.color.closeHoverBg },
    },
  },
};

/** "0 / 100" trailing the summary field. */
export const counterSx: SxProps<Theme> = {
  ml: t.field.adornmentGap,
  color: t.color.subtle,
  fontSize: t.font.counter,
  fontWeight: t.weight.regular,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

/* ── Description editor ──────────────────────────────────────────────────── */

/**
 * Bordered box holding the Quill toolbar above its body. Quill's snow theme
 * draws its own outline around each half; both are stripped so the shell
 * provides the single outline the design asks for — the same treatment the
 * ticket comment editor uses.
 */
export const editorShellSx = (hasError: boolean): SxProps<Theme> => ({
  bgcolor: t.color.surface,
  border: `1px solid ${hasError ? t.color.error : t.color.fieldBorder}`,
  borderRadius: t.radius.editor,
  overflow: "hidden",

  "& .ql-toolbar.ql-snow": {
    border: "none",
    borderBottom: `1px solid ${t.color.hairline}`,
    bgcolor: t.color.fieldBg,
    p: t.editor.toolbarPadding,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    fontFamily: "inherit",
  },
  // Quill groups its buttons into `.ql-formats`; the design rules the groups
  // off from one another rather than relying on whitespace alone.
  "& .ql-toolbar.ql-snow .ql-formats": {
    display: "inline-flex",
    alignItems: "center",
    m: 0,
    "&:not(:last-child)": {
      pr: t.editor.toolbarGroupGap,
      mr: t.editor.toolbarGroupGap,
      borderRight: `1px solid ${t.color.hairline}`,
    },
  },
  "& .ql-toolbar.ql-snow button": {
    width: t.editor.buttonSize,
    height: t.editor.buttonSize,
    p: 0,
    borderRadius: t.radius.toolbarButton,
    color: t.color.muted,
    "&:hover": { bgcolor: t.color.toolbarHoverBg },
  },
  "& .ql-toolbar.ql-snow button svg": {
    width: t.editor.buttonIconSize,
    height: t.editor.buttonIconSize,
  },
  // Quill paints its glyphs through explicit stroke / fill attributes, so the
  // active and hover states have to be recoloured on those rather than `color`.
  "& .ql-snow .ql-stroke": { stroke: "currentColor" },
  "& .ql-snow .ql-fill": { fill: "currentColor" },
  "& .ql-snow .ql-thin": { stroke: "currentColor" },
  "& .ql-toolbar.ql-snow button.ql-active, & .ql-toolbar.ql-snow button:hover":
    {
      color: t.color.accent,
    },

  "& .ql-container.ql-snow": {
    border: "none",
    fontFamily: "inherit",
    fontSize: t.font.field,
  },
  "& .ql-editor": {
    minHeight: t.editor.minHeight,
    p: t.editor.contentPadding,
    color: t.color.ink,
    fontSize: t.font.field,
    lineHeight: t.editor.lineHeight,
  },
  "& .ql-editor.ql-blank::before": {
    left: t.editor.contentPadding.split(" ")[1],
    right: t.editor.contentPadding.split(" ")[1],
    color: t.color.placeholder,
    fontStyle: "normal",
    fontSize: t.font.field,
  },
  ...(hasError
    ? {
        "& .ql-editor:focus-visible": {
          outline: "2px solid",
          outlineColor: "formField.focusBorder",
          outlineOffset: "-2px",
        },
      }
    : {}),
});

/* ── Attachments ─────────────────────────────────────────────────────────── */

/** Overrides `CustomDropzone`'s default 2px/26px well with the design's strip. */
export const dropzoneSx: SxProps<Theme> = {
  border: `1px dashed ${t.color.dropzoneBorder}`,
  borderRadius: t.radius.editor,
  bgcolor: t.color.dropzoneBg,
  textAlign: "left",
  cursor: "pointer",
  "&:hover": { borderColor: t.color.accent },
};

/** Paperclip and its caption on the left, the size ceiling on the right. */
export const dropzoneBodySx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: t.dropzone.gap,
  p: t.dropzone.padding,
};

export const dropzoneHintSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: t.dropzone.gap,
  minWidth: 0,
  color: t.color.muted,
  fontSize: t.font.hint,
  fontWeight: t.weight.regular,
  lineHeight: "normal",
};

export const dropzoneIconSx: SxProps<Theme> = {
  width: t.dropzone.iconSize,
  height: t.dropzone.iconSize,
  flexShrink: 0,
  color: t.color.subtle,
};

export const dropzoneStrongSx: SxProps<Theme> = {
  color: t.color.ink,
  fontWeight: t.weight.strong,
};

export const dropzoneBrowseSx: SxProps<Theme> = {
  color: t.color.accent,
  fontWeight: t.weight.strong,
};

export const dropzoneMetaSx: SxProps<Theme> = {
  flexShrink: 0,
  color: t.color.subtle,
  fontSize: t.font.hintMeta,
  fontWeight: t.weight.regular,
  lineHeight: "normal",
};

export const stagedFilesSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: t.dropzone.chipGap,
  mt: t.dropzone.chipsMarginTop,
};

export const stagedFileChipSx: SxProps<Theme> = {
  height: "26px",
  bgcolor: t.color.chipBg,
  color: t.color.chipText,
  borderRadius: t.radius.pill,
  fontSize: t.font.chip,
  fontWeight: t.weight.medium,
  "& .MuiChip-deleteIcon": {
    color: t.color.subtle,
    "&:hover": { color: t.color.ink },
  },
};

export const supportedFormatsSx: SxProps<Theme> = {
  display: "block",
  mt: t.dropzone.supportedMarginTop,
  color: t.color.subtle,
  fontSize: t.font.hintMeta,
  fontWeight: t.weight.regular,
  lineHeight: "normal",
};

/*
 * The footer bar and its two buttons come from the Modal System and the global
 * `MuiButton` theme — nothing to restate here.
 */
